import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { X, Loader2, Download, Send, CheckCircle2, AlertTriangle, Trash2, XCircle, Shield, ExternalLink, Bell, AlertOctagon, Clock, Pencil } from 'lucide-react';
import InvoiceCreateModal from './InvoiceCreateModal';

const fmtKr = (v) => v != null ? `${Number(v).toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr` : '—';

const STAGE_LABELS = {
  1: '1:a påminnelse',
  2: '2:a påminnelse',
  3: '3:e påminnelse (sista)',
  4: 'Överlämnad till inkasso',
};

const InvoiceDetailModal = ({ invoiceId, onClose, onChanged, autoOpenSign = false }) => {
  const { user } = useAuth();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSign, setShowSign] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [signingUrl, setSigningUrl] = useState('');
  const [showMarkPaid, setShowMarkPaid] = useState(false);
  const [paidAmount, setPaidAmount] = useState('');
  const [reminding, setReminding] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  // PIN-signering (alternativ till BankID)
  const [signingMethod, setSigningMethod] = useState('bankid');
  const [pinCode, setPinCode] = useState('');
  // Lägg till timmar (tidrapporter → fakturarader)
  const [showAttachTime, setShowAttachTime] = useState(false);
  const [billableReports, setBillableReports] = useState([]);
  const [selectedReportIds, setSelectedReportIds] = useState({});
  const [loadingBillable, setLoadingBillable] = useState(false);
  const [attaching, setAttaching] = useState(false);

  const loadBillable = async () => {
    setLoadingBillable(true);
    try {
      const params = {};
      if (inv?.customer_id) params.customer_id = inv.customer_id;
      if (inv?.project_id) params.project_id = inv.project_id;
      const res = await api.listBillableTimeReports(params);
      setBillableReports(res.data.time_reports || []);
      setSelectedReportIds({});
    } catch (err) {
      toast.error('Kunde inte hämta tidrapporter');
    } finally {
      setLoadingBillable(false);
    }
  };

  useEffect(() => {
    if (showAttachTime) loadBillable();
    /* eslint-disable-next-line */
  }, [showAttachTime]);

  const handleAttachTime = async () => {
    const ids = Object.entries(selectedReportIds).filter(([, v]) => v).map(([k]) => k);
    if (ids.length === 0) { toast.error('Välj minst en tidrapport'); return; }
    setAttaching(true);
    try {
      const res = await api.attachTimeReportsToInvoice(invoiceId, ids);
      toast.success(`${res.data.added} tidrapport(er) lades till fakturan`);
      setShowAttachTime(false);
      await fetchInv();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte lägga till timmar');
    } finally {
      setAttaching(false);
    }
  };

  const fetchSigningMethod = async () => {
    try {
      const res = await api.getSigningMethod();
      setSigningMethod(res.data.signing_method || 'bankid');
    } catch {
      // fallback silent
    }
  };

  const fetchInv = async () => {
    setLoading(true);
    try {
      const res = await api.getInvoice(invoiceId);
      setInv(res.data);
      setPaidAmount(res.data.total);
      if (user) {
        setSignerName(user.name || '');
        setSignerEmail(user.email || '');
      }
    } catch {
      toast.error('Kunde inte hämta faktura');
      onClose();
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchInv(); fetchSigningMethod(); /* eslint-disable-next-line */ }, [invoiceId]);

  // Öppna signeringsdialogen automatiskt om anropad från fakturalista-Skicka-knappen
  useEffect(() => {
    if (!autoOpenSign || !inv) return;
    if (inv.status === 'draft' || inv.status === 'awaiting_signature') {
      setShowSign(true);
    }
  }, [autoOpenSign, inv]);

  const handleSignSend = async () => {
    // PIN-flöde
    if (signingMethod === 'pin') {
      if (!/^\d{4,8}$/.test(pinCode)) {
        toast.error('PIN måste vara 4–8 siffror');
        return;
      }
      setSigning(true);
      try {
        await api.signInvoiceWithPin(invoiceId, { pin: pinCode });
        toast.success('Fakturan signerad och skickad till kund!');
        setPinCode('');
        setShowSign(false);
        onChanged?.();
        await fetchInv();
      } catch (err) {
        const msg = err.response?.data?.detail || 'Kunde inte signera med PIN';
        toast.error(msg);
      } finally { setSigning(false); }
      return;
    }
    // BankID-flöde (default)
    if (!signerName || !signerEmail) { toast.error('Fyll i namn och e-post'); return; }
    setSigning(true);
    try {
      const res = await api.signAndSendInvoice(invoiceId, { signer_name: signerName, signer_email: signerEmail });
      const url = res.data.signing_url;
      setSigningUrl(url);
      toast.success('Signering startad. Öppnar BankID...');
      try {
        const win = window.open(url, '_blank', 'noopener,noreferrer');
        if (!win || win.closed || typeof win.closed === 'undefined') {
          toast.info('Popup blockerad — klicka på "Öppna BankID-signering" nedan.');
        }
      } catch {
        toast.info('Klicka på knappen nedan för att öppna BankID.');
      }
      await fetchInv();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Kunde inte starta signering. Kontrollera bankgiro/IBAN i Inställningar → Fakturering.';
      toast.error(msg);
    } finally { setSigning(false); }
  };

  const handleMarkPaid = async () => {
    try {
      await api.markInvoicePaid(invoiceId, { paid_amount: parseFloat(paidAmount), payment_method: 'bankgiro' });
      toast.success('Markerad som betald');
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Misslyckades');
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Makulera fakturan? Detta går inte att ångra.')) return;
    try {
      await api.cancelInvoice(invoiceId);
      toast.success('Faktura makulerad');
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Misslyckades');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Ta bort utkastet?')) return;
    try {
      await api.deleteInvoice(invoiceId);
      toast.success('Borttagen');
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Misslyckades');
    }
  };

  const handleSendReminder = async (stage) => {
    const labelMap = { 1: 'vänlig', 2: 'bestämd', 3: 'sista (innan inkasso)' };
    if (!window.confirm(`Skicka ${labelMap[stage] || ''} påminnelse till kunden?`)) return;
    setReminding(true);
    try {
      const res = await api.sendInvoiceReminder(invoiceId, stage);
      toast.success(`${STAGE_LABELS[stage]} skickad (${res.data.days_overdue} dagar försenad)`);
      fetchInv();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Misslyckades');
    } finally { setReminding(false); }
  };

  const handleDebtCollection = async () => {
    if (!window.confirm('Markera som överlämnad till inkasso? Auto-påminnelser stoppas och ärendet markeras för manuell hantering.')) return;
    try {
      await api.markInvoiceDebtCollection(invoiceId);
      toast.success('Markerad för inkasso');
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Misslyckades');
    }
  };

  if (loading || !inv) {
    return (
      <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center">
        <Loader2 className="text-white animate-spin" />
      </div>
    );
  }

  const cust = inv.customer_snapshot || {};
  const canSign = inv.status === 'draft';
  const canMarkPaid = ['sent', 'overdue'].includes(inv.status);
  const canDelete = inv.status === 'draft';
  const canCancel = ['sent', 'overdue', 'awaiting_signature'].includes(inv.status);
  const canRemind = ['sent', 'overdue'].includes(inv.status) && (inv.reminder_stage || 0) < 4;
  const canDebtCollection = inv.status === 'overdue' && (inv.reminder_stage || 0) < 4;
  const nextStage = Math.min(3, (inv.reminder_stage || 0) + 1);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-3 py-6" onClick={onClose} data-testid="invoice-detail-modal">
      <div className="bg-card rounded-2xl shadow-2xl border border-border max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="bg-[#1A1A1A] text-white px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-white/60">Faktura</p>
            <h2 className="text-lg font-bold">{inv.invoice_number}</h2>
          </div>
          <div className="flex items-center gap-2">
            <a href={api.invoicePdfUrl(invoiceId)} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-full" data-testid="detail-pdf-link">
              <Download size={12} /> PDF
            </a>
            <button onClick={onClose} className="hover:bg-white/10 rounded p-1"><X size={16} /></button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto space-y-4">
          <StatusBanner inv={inv} signingUrl={signingUrl} />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card title="Kund">
              <p className="font-bold">{cust.company_name || cust.contact_person || '—'}</p>
              {cust.org_number && <p className="text-xs text-muted-foreground">Org.nr {cust.org_number}</p>}
              {cust.billing_address && <p className="text-xs text-muted-foreground">{cust.billing_address}</p>}
              <p className="text-xs text-muted-foreground">{cust.postal_code} {cust.city}</p>
              {cust.email && <p className="text-xs text-muted-foreground mt-1">{cust.email}</p>}
            </Card>
            <Card title="Faktura">
              <Row k="Fakturadatum" v={inv.issue_date} />
              <Row k="Förfallodatum" v={inv.due_date} />
              <Row k="OCR" v={inv.ocr} />
              <Row k="Villkor" v={inv.payment_terms} />
              {inv.reference && <Row k="Referens" v={inv.reference} />}
            </Card>
          </div>

          <Card title="Rader">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b border-border">
                <tr><th className="text-left py-2">Beskrivning</th><th className="text-right">Antal</th><th className="text-right">À-pris</th><th className="text-right">Moms</th><th className="text-right">Summa</th></tr>
              </thead>
              <tbody>
                {(inv.items || []).map((it, i) => (
                  <tr key={i} className="border-b border-border/40">
                    <td className="py-1.5">{it.description}</td>
                    <td className="text-right">{it.quantity} {it.unit}</td>
                    <td className="text-right">{fmtKr(it.unit_price)}</td>
                    <td className="text-right">{it.vat_rate}%</td>
                    <td className="text-right font-medium">{fmtKr(it.quantity * it.unit_price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-right mt-3 space-y-0.5">
              <p className="text-sm text-muted-foreground">Delsumma: <span className="text-foreground font-medium">{fmtKr(inv.subtotal)}</span></p>
              <p className="text-sm text-muted-foreground">Moms: <span className="text-foreground font-medium">{fmtKr(inv.vat_total)}</span></p>
              <p className="text-xl font-bold text-[#E8613A]">Att betala: {fmtKr(inv.total)}</p>
            </div>
          </Card>

          {inv.notes && <Card title="Meddelande"><p className="text-sm">{inv.notes}</p></Card>}

          {/* Reminders */}
          {(canRemind || (inv.reminder_log && inv.reminder_log.length > 0) || inv.reminder_stage === 4) && (
            <Card title="Påminnelser">
              {inv.reminder_stage === 4 && (
                <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg p-3 mb-3 flex items-start gap-2" data-testid="debt-collection-status">
                  <AlertOctagon size={16} className="text-red-700 dark:text-red-300 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-red-900 dark:text-red-200">Överlämnad till inkasso</p>
                    <p className="text-xs text-red-800 dark:text-red-300 mt-0.5">Auto-påminnelser stoppade. Hantera manuellt med ditt inkassobolag.</p>
                  </div>
                </div>
              )}

              {(inv.reminder_log && inv.reminder_log.length > 0) ? (
                <div className="space-y-1.5 mb-3" data-testid="reminder-log">
                  {inv.reminder_log.map((r, i) => (
                    <div key={i} className="flex items-center justify-between text-xs bg-muted/40 rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2">
                        <Bell size={11} className="text-[#E8613A]" />
                        <span className="font-medium">{r.label || STAGE_LABELS[r.stage]}</span>
                        {r.auto && <span className="text-[9px] uppercase bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded">Auto</span>}
                      </div>
                      <span className="text-muted-foreground">
                        <Clock size={9} className="inline mr-1" />
                        {new Date(r.sent_at).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' })}
                        {r.days_overdue > 0 && <span className="ml-2">({r.days_overdue}d försenad)</span>}
                      </span>
                    </div>
                  ))}
                </div>
              ) : canRemind ? (
                <p className="text-xs text-muted-foreground mb-3">Inga påminnelser skickade än. Auto-påminnelser skickas automatiskt: dag 0 (förfallodagen), dag +7, dag +14.</p>
              ) : null}

              {canRemind && (
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => handleSendReminder(nextStage)} disabled={reminding}
                    className="bg-[#E8613A] hover:bg-[#D4532F] text-white rounded-full h-9"
                    data-testid="send-next-reminder-btn">
                    {reminding ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Bell size={12} className="mr-1.5" />}
                    Skicka {STAGE_LABELS[nextStage]?.toLowerCase()}
                  </Button>
                  {canDebtCollection && (
                    <Button size="sm" variant="outline" onClick={handleDebtCollection}
                      className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 rounded-full h-9"
                      data-testid="debt-collection-btn">
                      <AlertOctagon size={12} className="mr-1.5" /> Överlämna till inkasso
                    </Button>
                  )}
                </div>
              )}
            </Card>
          )}
        </div>

        <div className="px-6 py-4 border-t border-border bg-muted/30 flex items-center justify-between flex-wrap gap-2">
          <div className="flex gap-2 flex-wrap">
            {canDelete && <Button variant="outline" size="sm" onClick={() => setShowEdit(true)} className="text-foreground" data-testid="edit-invoice-btn"><Pencil size={12} className="mr-1" /> Redigera</Button>}
            {canDelete && <Button variant="outline" size="sm" onClick={handleDelete} className="text-red-600 border-red-300 hover:bg-red-50" data-testid="del-invoice-btn"><Trash2 size={12} className="mr-1" /> Ta bort</Button>}
            {canCancel && <Button variant="outline" size="sm" onClick={handleCancel} className="text-red-600 border-red-300 hover:bg-red-50" data-testid="cancel-invoice-btn"><XCircle size={12} className="mr-1" /> Makulera</Button>}
          </div>
          <div className="flex gap-2 flex-wrap">
            {canMarkPaid && (
              <Button onClick={() => setShowMarkPaid(true)} className="bg-green-600 hover:bg-green-700 text-white rounded-full h-9" data-testid="mark-paid-btn">
                <CheckCircle2 size={14} className="mr-1.5" /> Markera betald
              </Button>
            )}
            {inv.status === 'draft' && (
              <Button onClick={() => setShowAttachTime(true)} variant="outline" size="sm" className="rounded-full h-9" data-testid="attach-time-btn">
                <Clock size={14} className="mr-1.5" /> Lägg till timmar
              </Button>
            )}
            {canSign && (
              <Button onClick={() => setShowSign(true)} className="bg-[#E8613A] hover:bg-[#D4532F] text-white rounded-full h-9" data-testid="sign-send-btn">
                <Shield size={14} className="mr-1.5" />
                {signingMethod === 'pin' ? 'Signera med PIN & skicka' : 'Signera med BankID & skicka'}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Sign dialog */}
      {showSign && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-3" onClick={() => !signing && setShowSign(false)}>
          <div className="bg-card rounded-xl border border-border max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="sign-dialog">
            <h3 className="text-base font-bold flex items-center gap-2 mb-3">
              <Shield size={16} className="text-[#E8613A]" />
              {signingMethod === 'pin' ? 'Signera med PIN-kod' : 'Signera & skicka faktura'}
            </h3>
            {signingMethod === 'pin' ? (
              /* ---- PIN-flöde ---- */
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Skriv in din 4–8-siffriga PIN. När korrekt skickas fakturan direkt till kundens e-post med bankgiro-uppgifter.
                </p>
                <div>
                  <Label className="text-xs">PIN-kod *</Label>
                  <Input
                    type="password"
                    inputMode="numeric"
                    pattern="\d*"
                    autoComplete="off"
                    value={pinCode}
                    onChange={(e) => setPinCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                    className="mt-1 tracking-widest text-center text-lg font-mono"
                    placeholder="••••"
                    data-testid="pin-input"
                    autoFocus
                  />
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Ingen PIN satt än? Gå till <strong>Inställningar → Fakturering → Signering</strong>.
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowSign(false)} className="flex-1" disabled={signing}>Avbryt</Button>
                  <Button onClick={handleSignSend} disabled={signing || pinCode.length < 4} className="flex-1 bg-[#E8613A] hover:bg-[#D4532F] text-white" data-testid="pin-sign-btn">
                    {signing ? <Loader2 size={14} className="animate-spin" /> : <>Signera & skicka</>}
                  </Button>
                </div>
              </div>
            ) : signingUrl ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">Signera nu med BankID. När du signerat skickas fakturan automatiskt till kundens e-post.</p>
                <a href={signingUrl} target="_blank" rel="noreferrer" className="block w-full text-center bg-[#E8613A] hover:bg-[#D4532F] text-white rounded-full py-3 font-semibold" data-testid="open-bankid">
                  <ExternalLink size={14} className="inline mr-1.5" /> Öppna BankID-signering
                </a>
                <Button variant="outline" onClick={() => { setShowSign(false); onChanged?.(); }} className="w-full">Stäng</Button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">Du signerar fakturan med BankID. När signering är klar skickas PDF + bankgiro-uppgifter automatiskt till kunden.</p>
                <div>
                  <Label className="text-xs">Ditt namn *</Label>
                  <Input value={signerName} onChange={(e) => setSignerName(e.target.value)} className="mt-1" data-testid="signer-name" />
                </div>
                <div>
                  <Label className="text-xs">Din e-post *</Label>
                  <Input type="email" value={signerEmail} onChange={(e) => setSignerEmail(e.target.value)} className="mt-1" data-testid="signer-email" />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowSign(false)} className="flex-1" disabled={signing}>Avbryt</Button>
                  <Button onClick={handleSignSend} disabled={signing} className="flex-1 bg-[#E8613A] hover:bg-[#D4532F] text-white" data-testid="start-signing-btn">
                    {signing ? <Loader2 size={14} className="animate-spin" /> : <>Starta signering</>}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mark paid dialog */}
      {showMarkPaid && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-3" onClick={() => setShowMarkPaid(false)}>
          <div className="bg-card rounded-xl border border-border max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="mark-paid-dialog">
            <h3 className="text-base font-bold mb-3">Markera som betald</h3>
            <Label className="text-xs">Inbetalt belopp</Label>
            <Input type="number" value={paidAmount} onChange={(e) => setPaidAmount(e.target.value)} className="mt-1 mb-4" data-testid="paid-amount" />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowMarkPaid(false)} className="flex-1">Avbryt</Button>
              <Button onClick={handleMarkPaid} className="flex-1 bg-green-600 hover:bg-green-700 text-white" data-testid="confirm-mark-paid">
                <CheckCircle2 size={14} className="mr-1" /> Bekräfta
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit draft modal */}
      {showEdit && (
        <InvoiceCreateModal
          editInvoice={inv}
          onClose={() => setShowEdit(false)}
          onCreated={() => { setShowEdit(false); fetchInv(); onChanged?.(); }}
        />
      )}

      {/* Attach time reports dialog */}
      {showAttachTime && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-3" onClick={() => !attaching && setShowAttachTime(false)}>
          <div className="bg-card rounded-xl border border-border max-w-2xl w-full p-5 max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="attach-time-dialog">
            <h3 className="text-base font-bold flex items-center gap-2 mb-2">
              <Clock size={16} className="text-[#E8613A]" /> Lägg till timmar på fakturan
            </h3>
            <p className="text-xs text-muted-foreground mb-4">
              Godkända, ej-fakturerade tidrapporter{inv.project_id ? ' för detta projekt' : ''}. Timkostnaden hämtas från projekt eller anställd.
            </p>
            <div className="flex-1 overflow-y-auto border border-border rounded-lg">
              {loadingBillable ? (
                <div className="text-center text-muted-foreground py-6 text-sm">Laddar…</div>
              ) : billableReports.length === 0 ? (
                <div className="text-center text-muted-foreground py-6 text-sm" data-testid="no-billable-reports">
                  Inga godkända, ej-fakturerade tidrapporter hittades.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="text-left px-3 py-2 w-8"></th>
                      <th className="text-left px-3 py-2">Datum</th>
                      <th className="text-left px-3 py-2">Anställd</th>
                      <th className="text-left px-3 py-2">Projekt</th>
                      <th className="text-right px-3 py-2">Timmar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billableReports.map((r) => (
                      <tr key={r.id} className="border-t border-border hover:bg-muted/30 cursor-pointer"
                          onClick={() => setSelectedReportIds({ ...selectedReportIds, [r.id]: !selectedReportIds[r.id] })}
                          data-testid={`billable-row-${r.id}`}>
                        <td className="px-3 py-2">
                          <input type="checkbox" checked={!!selectedReportIds[r.id]} onChange={() => {}} />
                        </td>
                        <td className="px-3 py-2 font-medium">{r.date}</td>
                        <td className="px-3 py-2 text-muted-foreground">{r.employee_name}</td>
                        <td className="px-3 py-2 text-muted-foreground">{r.project_name || '—'}</td>
                        <td className="px-3 py-2 text-right font-semibold">{r.hours?.toFixed?.(1) || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowAttachTime(false)} className="flex-1" disabled={attaching}>Avbryt</Button>
              <Button
                onClick={handleAttachTime}
                disabled={attaching || Object.values(selectedReportIds).filter(Boolean).length === 0}
                className="flex-1 bg-[#E8613A] hover:bg-[#D4532F] text-white"
                data-testid="confirm-attach-time">
                {attaching ? <Loader2 size={14} className="animate-spin" /> : `Lägg till ${Object.values(selectedReportIds).filter(Boolean).length} tidrapport(er)`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatusBanner = ({ inv, signingUrl }) => {
  const map = {
    draft: { icon: AlertTriangle, color: 'amber', title: 'Utkast', body: 'Signera med BankID för att skicka fakturan till kunden.' },
    awaiting_signature: { icon: Shield, color: 'amber', title: 'Inväntar signering', body: 'BankID-signering pågår. När den slutförts skickas fakturan automatiskt.' },
    sent: { icon: Send, color: 'blue', title: 'Skickad', body: `Skickad till kund. Förfaller ${inv.due_date}.` },
    paid: { icon: CheckCircle2, color: 'green', title: 'Betald', body: `Betald ${(inv.paid_at || '').split('T')[0] || ''} via ${inv.payment_method || 'bankgiro'}.` },
    overdue: { icon: AlertTriangle, color: 'red', title: 'Förfallen!', body: `Skulle ha betalats ${inv.due_date}.` },
    cancelled: { icon: XCircle, color: 'gray', title: 'Makulerad', body: 'Denna faktura är makulerad.' },
  };
  const cfg = map[inv.status] || map.draft;
  const Icon = cfg.icon;
  const colors = {
    amber: 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900 text-amber-900 dark:text-amber-200',
    blue: 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900 text-blue-900 dark:text-blue-200',
    green: 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-900 text-green-900 dark:text-green-200',
    red: 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900 text-red-900 dark:text-red-200',
    gray: 'bg-muted border-border text-muted-foreground',
  };
  return (
    <div className={`border rounded-xl p-3 flex items-start gap-3 ${colors[cfg.color]}`} data-testid="status-banner">
      <Icon size={18} className="flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold">{cfg.title}</p>
        <p className="text-xs opacity-90 mt-0.5">{cfg.body}</p>
        {signingUrl && inv.status === 'awaiting_signature' && (
          <a href={signingUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs font-bold underline">
            <ExternalLink size={11} /> Öppna BankID-länk
          </a>
        )}
      </div>
    </div>
  );
};

const Card = ({ title, children }) => (
  <div className="bg-muted/30 border border-border rounded-xl p-4">
    <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-2">{title}</p>
    {children}
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex justify-between text-sm mb-0.5">
    <span className="text-muted-foreground">{k}:</span>
    <span className="font-medium">{v || '—'}</span>
  </div>
);

export default InvoiceDetailModal;
